import React, { useState } from 'react';
import { Badge, Button, Nav } from 'react-bootstrap';
import useShareRequestTabs from 'hooks/useShareRequestTabs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import corner6 from 'assets/img/illustrations/corner-6.png';
import FalconComponentCard from 'components/common/FalconComponentCard';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import AssociationRequestModal from 'components/common/AssociationRequestModal';
import AssociationsShareTab from './AssociationsShareTab';
import { buildShareRequestAccessPayload } from 'util/shareRequestAccess';
import {
  SHARE_REQUEST_TAGS,
  useCreateShareRequestMutation,
  useSearchUserByEmailMutation,
  shareRequestApi
} from 'rtk/org-admin/share-request.slice';

const Associations = ({ ...rest }) => {
  const dispatch = useDispatch();
  const statusOptions = [
    'PENDING',
    'REJECTED',
    'EXPIRED',
    'CANCELLED',
    'ACCEPTED',
    'ALL'
  ];
  const { activeTab, selectTab, sentCount, receivedCount, targetGuid } =
    useShareRequestTabs();
  const [tabLoading, setTabLoading] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);

  const [searchUserByEmail, { isLoading: searching }] =
    useSearchUserByEmailMutation();
  const [createShareRequest, { isLoading: sending }] =
    useCreateShareRequestMutation();

  const handleRefresh = () => {
    dispatch(
      shareRequestApi.util.invalidateTags([
        { type: SHARE_REQUEST_TAGS.SHARE_REQUEST, id: 'LIST' }
      ])
    );
  };

  const openSendRequestModal = () => {
    setShowSendModal(true);
  };

  const handleSubmitSend = async ({
    email,
    accessType,
    scopeFromDate,
    scopeToDate
  }) => {
    try {
      const res = await searchUserByEmail(email).unwrap();
      const guid = res?.GUID;
      if (!guid) {
        toast.error('User not found for this email.');
        return;
      }
      const payload = {
        ToUserMasterGuid: guid,
        ...buildShareRequestAccessPayload(accessType, {
          scopeFromDate,
          scopeToDate
        })
      };
      await createShareRequest(payload).unwrap();
      toast.success('Connection request sent.');
      setShowSendModal(false);
    } catch (err) {
      toast.error(_.get(err, 'Text', 'Failed to send connection request.'));
    }
  };

  return (
    <>
      {/* <PageHeader
        title="Associations"
        description="Manage your association requests and connections."
        titleTag="h2"
        className="mb-3"
        image={corner6}
        col={{ lg: 8, md: 7, sm: 12 }}
      /> */}

      <FalconComponentCard noGuttersBottom {...rest}>
        <FalconComponentCard.Header
          image={corner6}
          className="px-4"
          title="Associations"
          subtitle="Manage your association requests and connections"
          onRefresh={handleRefresh}
          loading={tabLoading}
          endEl={
            <Button variant="primary" onClick={openSendRequestModal}>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Send Request
            </Button>
          }
        />
        <FalconComponentCard.Body noLight className="py-0 px-4 fs--1">
          <Nav
            variant="tabs"
            activeKey={activeTab}
            onSelect={selectTab}
            className="mb-3"
          >
            <Nav.Item>
              <Nav.Link eventKey="sent">
                Sent{' '}
                {sentCount > 0 && (
                  <Badge bg="secondary" title="Pending sent requests">
                    {sentCount}
                  </Badge>
                )}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="received">
                Received{' '}
                {receivedCount > 0 && (
                  <Badge bg="primary" title="Pending received requests">
                    {receivedCount}
                  </Badge>
                )}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {activeTab === 'sent' && (
            <AssociationsShareTab
              key={`sent:${targetGuid || ''}`}
              direction="SENT"
              statusOptions={statusOptions}
              onLoadingChange={setTabLoading}
            />
          )}

          {activeTab === 'received' && (
            <AssociationsShareTab
              key={`received:${targetGuid || ''}`}
              direction="RECEIVED"
              statusOptions={statusOptions}
              onLoadingChange={setTabLoading}
            />
          )}
        </FalconComponentCard.Body>

        <AssociationRequestModal
          show={showSendModal}
          onHide={() => !sending && setShowSendModal(false)}
          title="Send Connection Request"
          emailLabel="Notary email address"
          emailPlaceholder="email@example.com"
          showAccessType={true}
          sending={sending || searching}
          onSubmit={handleSubmitSend}
          cancelLabel="Cancel"
          submitLabel="Send Request"
          showLocationDropdown={false}
        />
      </FalconComponentCard>
    </>
  );
};

export default Associations;
